/**
 * The cabinet's wire half — contracts #26–#26.6 (wave 9, SR §3.4).
 *
 *   #26    GET    /api/curator/venues              → `{data:{venues:[…]}}`
 *   #26.1  GET    /api/curator/children-offline    → `{data:{children:[…]}}`
 *   #26.2  GET    /api/curator/prefill             → `{data:{rows:[…]}, meta}`
 *   #26.3  POST   /api/curator/prefill             → `{data:{row}}`
 *   #26.4  DELETE /api/curator/prefill/{rowId}     → 204
 *   #26.5  PATCH  /api/curator/prefill/{rowId}     → `{data:{row}}`
 *   #26.6  GET    /api/curator/export              → CSV (BOM, `;`) or, since contract
 *                                                          v1.8.1, `?format=xlsx` — the
 *                                                          same nine columns as a workbook
 *   #26.7  POST   /api/curator/prefill/import      → the per-row import report (ADDITIVE
 *                                                          contract v1.8.9, T.Z46) — an
 *                                                          .xlsx/.csv of the same five
 *                                                          columns onto ONE venue
 *
 * Every route sits behind the cabinet guard (`@ExternalAccess` + `CuratorAccessGuard`):
 * an `email_code` session gets its own venues and nothing else, and any explicit id
 * outside that area is a loud 403 `CURATOR_SCOPE_REQUIRED`, not an empty page.
 *
 * The list reads stay RAW (`unknown` out) because unwrapping is pure logic and the
 * `unit` project tests it in `./curator-model`; the writes unwrap in place — nothing
 * unit-tests a mutation's body. `#26.6` is a FILE, so it fetches its own bytes the
 * way `./exports.ts` does and hands them to the browser as a download.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, ApiError, apiRequest, buildQuery, requestEnvelope } from './client';
import { parseErrorBody, statusCodeToCode, type ApiErrorMeta } from './envelope';
import {
  CURATOR_CHILDREN_OFFLINE_PATH,
  CURATOR_EXPORT_PATH,
  CURATOR_PREFILL_IMPORT_PATH,
  CURATOR_PREFILL_PATH,
  CURATOR_VENUES_PATH,
  curatorExportQuery,
  parseCuratorPrefillImportReport,
  parseCuratorPrefillPage,
  parseCuratorPrefillRow,
  prefillQuery,
  PREFILL_MAX_PAGE_SIZE,
  type CuratorPageMeta,
  type CuratorPrefillImportReport,
  type CuratorPrefillRow,
  type CuratorExportFormat,
  type CuratorExportSection,
  type PrefillFilters,
} from './curator-model';

export type {
  CuratorChild,
  CuratorExportFormat,
  CuratorExportSection,
  CuratorMatchStatus,
  CuratorMissReason,
  CuratorPageMeta,
  CuratorPartnerRef,
  CuratorPrefillRow,
  CuratorVenue,
  CuratorVenueKind,
  PrefillFilters,
  PrefillStatusFilter,
} from './curator-model';
export {
  CURATOR_CHILDREN_OFFLINE_PATH,
  CURATOR_EXPORT_PATH,
  CURATOR_PREFILL_PATH,
  CURATOR_VENUES_PATH,
  MISS_REASON_LABEL,
  PREFILL_DEFAULT_PAGE_SIZE,
  PREFILL_MAX_PAGE_SIZE,
  PREFILL_SEARCH_DEBOUNCE_MS,
  PREFILL_SEARCH_PARAM,
  PREFILL_STATUS_OPTIONS,
  canExportCsv,
  curatorErrorCopy,
  curatorExportQuery,
  defaultVenueId,
  hasPrefillVenue,
  isPrefillStatusFilter,
  missReasonLabel,
  parseCuratorChildren,
  parseCuratorPrefillPage,
  parseCuratorPrefillRow,
  parseCuratorVenues,
  parsePrefillFilters,
  partnerPool,
  prefillQuery,
  rowStatusView,
  splitByAssignment,
  statusLabelOf,
  statusToneOf,
} from './curator-model';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** #26 — the venues of the session's area (RAW; `parseCuratorVenues` unwraps). */
export async function fetchCuratorVenues(signal?: AbortSignal): Promise<unknown> {
  return apiRequest<unknown>('GET', CURATOR_VENUES_PATH, { signal });
}

/** #26.1 — one offline venue's roster (RAW; `parseCuratorChildren` unwraps). */
export async function fetchCuratorChildrenOffline(venueId: number, signal?: AbortSignal): Promise<unknown> {
  return apiRequest<unknown>('GET', `${CURATOR_CHILDREN_OFFLINE_PATH}${buildQuery({ venueId })}`, { signal });
}

/** #26.2 — one page of the prefill rows, filters server-side (RAW out). */
export async function fetchCuratorPrefill(
  filters: PrefillFilters,
  signal?: AbortSignal,
): Promise<{ rows: CuratorPrefillRow[]; meta: CuratorPageMeta | null }> {
  const body = await apiRequest<unknown>('GET', `${CURATOR_PREFILL_PATH}${buildQuery(prefillQuery(filters))}`, { signal });
  // The transport returns the raw envelope; the model reads `{data:{rows}, meta}`.
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const source = typeof record.data === 'object' && record.data !== null ? record.data : record;
  return parseCuratorPrefillPage(source, record.meta);
}

/**
 * T.Z24 — the duplicate gate's read: every live row of the SCOPE whose ФИО matches,
 * before a create or an edit-save goes out. It is the same `#26.2` read the working
 * table uses — `?q=` (v1.8.2) pinned to the normalised ФИО, and deliberately NO
 * `venueId`: the natural key is per venue, and the duplicate the owner asked to
 * restrict is the same child twice anywhere in the curator's scope (the flagged
 * venues «Все места» shows), not only on the one being typed into.
 *
 * `?q=` is a CONTAINS over `full_name_norm`, so what comes back is a CANDIDATE set,
 * never a verdict — `classifyPrefillDuplicates` (`pages/cabinet/prefill-duplicates`)
 * does the exact-ФИО comparison and the block/warn rules on top of it.
 *
 * Pages are followed until `meta.total` is covered (a scope-wide check cannot be
 * trusted off its first page), bounded at {@link PREFILL_DUPLICATE_MAX_PAGES} so a
 * pathological scope cannot turn one submit into an unbounded loop.
 */
export const PREFILL_DUPLICATE_PAGE_SIZE = PREFILL_MAX_PAGE_SIZE;
export const PREFILL_DUPLICATE_MAX_PAGES = 5;

export async function fetchCuratorPrefillCandidates(fio: string, signal?: AbortSignal): Promise<CuratorPrefillRow[]> {
  const hits: CuratorPrefillRow[] = [];
  for (let page = 1; page <= PREFILL_DUPLICATE_MAX_PAGES; page += 1) {
    const { rows, meta } = await fetchCuratorPrefill({ q: fio, page, pageSize: PREFILL_DUPLICATE_PAGE_SIZE }, signal);
    hits.push(...rows);
    if (rows.length === 0 || hits.length >= (meta?.total ?? rows.length)) return hits;
  }
  return hits;
}

/**
 * T.Z78 — the ONLINE half of the group «Добавить» list: the venue's #26.2 rows,
 * matched and unmatched, paged the same bounded way the duplicate gate pages its
 * scope-wide read (`PREFILL_MAX_PAGE_SIZE` per call, {@link PREFILL_CANDIDATE_MAX_PAGES}
 * calls — a venue's online set cannot turn one picker into an unbounded loop). No
 * `?status=`: the assignable rows are the MATCHED ones and the hint needs to know the
 * UNMATCHED ones exist, so both arrive and the fold sorts them apart.
 */
export const PREFILL_CANDIDATE_MAX_PAGES = 5;

export async function fetchCuratorPrefillVenueRows(venueId: number, signal?: AbortSignal): Promise<CuratorPrefillRow[]> {
  const rows: CuratorPrefillRow[] = [];
  for (let page = 1; page <= PREFILL_CANDIDATE_MAX_PAGES; page += 1) {
    const { rows: pageRows, meta } = await fetchCuratorPrefill(
      { venueId, page, pageSize: PREFILL_MAX_PAGE_SIZE },
      signal,
    );
    rows.push(...pageRows);
    if (pageRows.length === 0 || rows.length >= (meta?.total ?? pageRows.length)) return rows;
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Body of #26.3 — exactly the keys the contract names. */
export interface CuratorPrefillCreateInput {
  venueId: number;
  fullName: string;
  pguId?: string;
  region?: string;
  untiId?: string;
  programName?: string;
  /**
   * ADDITIVE (contract v1.8.8, T.Z43) — the PREFERRED programme field; an unknown id
   * answers 400. The audience/format are the course's own properties (#21) — never sent.
   */
  courseId?: number;
}

/** Body of #26.5 — only provided keys change; `null` clears an optional field. */
export interface CuratorPrefillPatchInput {
  fullName?: string;
  region?: string | null;
  pguId?: string | null;
  untiId?: string | null;
  programName?: string | null;
  /** ADDITIVE (contract v1.8.8, T.Z43) — set / change the course; `null` clears it. */
  courseId?: number | null;
}

function prefillRowPath(rowId: number): string {
  return `${CURATOR_PREFILL_PATH}/${encodeURIComponent(rowId)}`;
}

/** #26.3 — the answered row is the projection the table already knows how to render. */
export async function createCuratorPrefillRow(input: CuratorPrefillCreateInput): Promise<CuratorPrefillRow> {
  const envelope = await requestEnvelope<{ row?: CuratorPrefillRow }>('POST', CURATOR_PREFILL_PATH, ['data'], {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const row = parseCuratorPrefillRow(envelope.data);
  if (row === null) throw new ApiError(500, 'POST', CURATOR_PREFILL_PATH, 'INTERNAL', 'Ответ сервера не содержит строки.');
  return row;
}

export async function patchCuratorPrefillRow(rowId: number, input: CuratorPrefillPatchInput): Promise<CuratorPrefillRow> {
  const envelope = await requestEnvelope<{ row?: CuratorPrefillRow }>('PATCH', prefillRowPath(rowId), ['data'], {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  const row = parseCuratorPrefillRow(envelope.data);
  if (row === null) throw new ApiError(500, 'PATCH', prefillRowPath(rowId), 'INTERNAL', 'Ответ сервера не содержит строки.');
  return row;
}

export async function deleteCuratorPrefillRow(rowId: number): Promise<void> {
  await apiRequest<void>('DELETE', prefillRowPath(rowId), { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// #26.6 — the file download (xlsx since contract v1.8.1; csv stays the api default)
// ---------------------------------------------------------------------------

export const CURATOR_EXPORT_MIME = 'text/csv';
/** The workbook MIME the api answers `?format=xlsx` with — the #18 one. */
export const CURATOR_EXPORT_XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Downloads the curator's own list. The projection is the server's business (the
 * «export == what the screen shows» test lives in the api); here only the bytes
 * move, named after the `Content-Disposition` the api sets.
 *
 * T.Y18 asks for xlsx (contract v1.8.1): the button hands the curator a workbook
 * Excel opens without an import wizard. The api's default remains `csv`, so a
 * caller that sends no `format` keeps the v1.8.0 file.
 *
 * T.Z14/Z5 adds the SCOPE: `venueId` (contract v1.8.2) narrows the file to the one
 * venue the picker has chosen — the button lives in that venue's table header, so
 * «the file of what this screen shows» is now literally true per venue. «Все места»
 * passes none and gets the whole scope, byte-for-byte the v1.8.1 answer.
 *
 * T.Z47 (contract v1.8.9) adds the SECTION: `section: 'offline'` downloads the
 * venue's #26.1 roster instead of the prefill rows — the offline block's own button
 * in `CabinetPage`. The api answers that section with the same six columns the
 * offline table renders, names the file `curator-offline-…`, and refuses it with
 * #26.1's own 403/400, so the caller always pins the venue it is looking at.
 */
export async function downloadCuratorExport(format: CuratorExportFormat = 'xlsx', venueId?: number, section?: CuratorExportSection): Promise<string> {
  const response = await fetch(`${API_BASE_URL}${CURATOR_EXPORT_PATH}${curatorExportQuery(format, venueId, section)}`, {
    headers: { Accept: format === 'xlsx' ? CURATOR_EXPORT_XLSX_MIME : CURATOR_EXPORT_MIME },
  });
  if (!response.ok) {
    if (response.status === 401) {
      window.location.assign(`${window.location.origin}${API_BASE_URL}/auth/login`);
    }
    let code = statusCodeToCode(response.status);
    let message = response.statusText || 'неизвестная ошибка';
    try {
      const parsed = parseErrorBody(await response.json(), response.status);
      code = parsed.code;
      message = parsed.message;
    } catch {
      // a failing file endpoint may answer non-JSON — the status is the message then
    }
    throw new ApiError(response.status, 'GET', CURATOR_EXPORT_PATH, code, message);
  }

  const blob = await response.blob();
  const filename =
    filenameFromDisposition(response.headers.get('Content-Disposition')) ??
    (section === 'offline' ? `curator-offline.${format}` : `curator-prefill.${format}`);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return filename;
}

/** `attachment; filename="curator-prefill-2026-09-12.csv"` → the file name. */
export function filenameFromDisposition(header: string | null): string | null {
  if (header === null) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  const encoded = utf8?.[1];
  if (encoded !== undefined) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const VENUES_ROOT = ['curator', 'venues'] as const;
const PREFILL_ROOT = ['curator', 'prefill'] as const;
const CHILDREN_ROOT = ['curator', 'children-offline'] as const;

/** #26 — the cabinet's spine: the venue set decides every section below it. */
export function useCuratorVenues() {
  return useQuery({
    queryKey: VENUES_ROOT,
    queryFn: ({ signal }) => fetchCuratorVenues(signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

/** #26.2 — the filters are the cache key: a filter change is a fresh read. */
export function useCuratorPrefill(filters: PrefillFilters) {
  return useQuery({
    queryKey: [...PREFILL_ROOT, filters],
    queryFn: ({ signal }) => fetchCuratorPrefill(filters, signal),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
    retry: 0,
  });
}

/** #26.1 — the offline roster of ONE venue, only while a venue is picked. */
export function useCuratorChildrenOffline(venueId: number | null) {
  return useQuery({
    queryKey: [...CHILDREN_ROOT, venueId],
    queryFn: ({ signal }) => fetchCuratorChildrenOffline(venueId as number, signal),
    enabled: venueId !== null,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

const PREFILL_VENUE_ROOT = ['curator', 'prefill-venue'] as const;

/**
 * T.Z78 — the venue's #26.2 rows for the group «Добавить» list, its own cache key so
 * it never shares an entry with the working table's filtered reads (and so the
 * cabinet-root invalidation the membership writes already run — `['curator']` —
 * refreshes it with the rest).
 */
export function useCuratorPrefillVenueRows(venueId: number) {
  return useQuery({
    queryKey: [...PREFILL_VENUE_ROOT, venueId],
    queryFn: ({ signal }) => fetchCuratorPrefillVenueRows(venueId, signal),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
    retry: 0,
  });
}

function useInvalidateCabinet(): () => void {
  const queryClient = useQueryClient();
  return () => {
    // A write changes the rows, the summary counters AND the partner pool, so the
    // whole cabinet root is re-read — three small queries, not a stale number.
    void queryClient.invalidateQueries({ queryKey: ['curator'] });
  };
}

export function useCuratorPrefillCreate() {
  const invalidate = useInvalidateCabinet();
  return useMutation({
    mutationFn: (input: CuratorPrefillCreateInput) => createCuratorPrefillRow(input),
    onSettled: invalidate,
  });
}

export function useCuratorPrefillPatch() {
  const invalidate = useInvalidateCabinet();
  return useMutation({
    mutationFn: (input: { rowId: number; patch: CuratorPrefillPatchInput }) =>
      patchCuratorPrefillRow(input.rowId, input.patch),
    onSettled: invalidate,
  });
}

export function useCuratorPrefillDelete() {
  const invalidate = useInvalidateCabinet();
  return useMutation({
    mutationFn: (rowId: number) => deleteCuratorPrefillRow(rowId),
    onSettled: invalidate,
  });
}

// ---------------------------------------------------------------------------
// #26.7 — «Загрузить из файла» (ADDITIVE contract v1.8.9, T.Z46; renamed by T.Z63)
// ---------------------------------------------------------------------------

/**
 * One multipart upload — the plain-`fetch` transport for the same reason #19's is:
 * the browser must set the multipart boundary itself (no `Content-Type` header of
 * ours), and the error body has to be read in full to keep `meta.limit` /
 * `meta.hint`, which the shared normaliser flattens away. The report is the answer
 * (202), parsed defensively; a refusal surfaces as the shared `ApiError`.
 */
export async function importCuratorPrefillFile(file: File, venueId: number): Promise<CuratorPrefillImportReport> {
  const form = new FormData();
  form.set('venueId', String(venueId));
  form.set('file', file, file.name);

  const response = await fetch(`${API_BASE_URL}${CURATOR_PREFILL_IMPORT_PATH}`, {
    method: 'POST',
    body: form,
  });

  if (!response.ok) {
    let code = statusCodeToCode(response.status);
    let message = response.statusText || 'неизвестная ошибка';
    let meta: ApiErrorMeta | undefined;
    try {
      const body = (await response.json()) as { error?: { code?: unknown; message?: unknown; meta?: unknown } };
      const error = body.error;
      if (typeof error?.code === 'string') code = error.code as ApiError['code'];
      if (typeof error?.message === 'string') message = error.message;
      if (typeof error?.meta === 'object' && error.meta !== null) meta = error.meta as ApiErrorMeta;
    } catch {
      // a refusal may answer non-JSON — the status is the message then
    }
    throw new ApiError(response.status, 'POST', CURATOR_PREFILL_IMPORT_PATH, code, message, meta);
  }

  const body = (await response.json()) as Record<string, unknown>;
  const report = parseCuratorPrefillImportReport(body['data']);
  if (report === null) {
    throw new ApiError(500, 'POST', CURATOR_PREFILL_IMPORT_PATH, 'INTERNAL', 'Ответ сервера не содержит отчёта об импорте.');
  }
  return report;
}

/** #26.7 — the upload refetches the cabinet root: rows, counters and the pool moved. */
export function useCuratorPrefillImport() {
  const invalidate = useInvalidateCabinet();
  return useMutation({
    mutationFn: (input: { file: File; venueId: number }) => importCuratorPrefillFile(input.file, input.venueId),
    onSettled: invalidate,
  });
}
